import axios from 'axios';
import posthog from 'posthog-js';
import type { CaptureOptions } from 'posthog-js';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { setToken } from '@/Config/auth/authStore';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { consumeAuthCallbackParams } from '@/Domain/utils/authCallbackParams';
import { getSafeNextPath, OAUTH_NEXT_STORAGE_KEY } from '@/Domain/utils/safeNextPath';

const CONSUME_NONCE_ENDPOINT = '/v1/api/auth/consume_callback_nonce';

// Bounds the spinner: without it a nonce request that never settles is indistinguishable
// from a closed tab, which is the question this screen's instrumentation exists to answer.
const CONSUME_NONCE_TIMEOUT_MS = 15000;
const AXIOS_TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

type AuthCallbackOutcome =
  | 'no_nonce'
  | 'consume_nonce_failed'
  | 'consume_nonce_timed_out'
  | 'no_token_returned'
  | 'token_store_failed'
  | 'redirected_to_next'
  | 'navigated_to_scenario'
  | 'handoff_to_index'
  | 'abandoned_on_spinner';

export function AuthCallbackPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const startedAt = Date.now();
    // authUrlPreflight already lifted these off the URL at boot, ahead of analytics init.
    const params = consumeAuthCallbackParams();
    const scenarioId = params.scenario_id;
    const nonce = params.nonce;

    // Analytics must never break login, so every capture here is swallowed.
    const track = (
      event: PostHogEventEnum,
      properties: Record<string, unknown>,
      options?: CaptureOptions,
    ) => {
      try {
        // Not `capture(e, p, options)` with options undefined: the default batched path
        // is the common case and should keep its own two-argument call shape.
        if (options) {
          posthog.capture(event, properties, options);
        } else {
          posthog.capture(event, properties);
        }
      } catch {
        /* empty */
      }
    };

    // Only after the fragment is gone: posthog-js stamps `$current_url` on every event.
    track(PostHogEventEnum.AuthCallbackViewed, {
      has_nonce: Boolean(nonce),
      has_scenario_id: Boolean(scenarioId),
    });

    let settled = false;
    const reportOutcome = (outcome: AuthCallbackOutcome, options?: CaptureOptions) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('pagehide', reportAbandonment);
      track(
        PostHogEventEnum.AuthCallbackOutcome,
        {
          outcome,
          duration_ms: Date.now() - startedAt,
          has_scenario_id: Boolean(scenarioId),
        },
        options,
      );
    };

    // initPostHog runs at boot, so posthog's own pagehide flush is registered before this
    // one and has already drained the queue by the time we get here — an enqueued event
    // would die with the page. The other outcomes queue before their navigation, so they
    // are picked up by that flush and need no override.
    function reportAbandonment(): void {
      reportOutcome('abandoned_on_spinner', { transport: 'sendBeacon', send_instantly: true });
    }
    window.addEventListener('pagehide', reportAbandonment);

    const bounceToLogin = (outcome: AuthCallbackOutcome) => {
      reportOutcome(outcome);
      toast.info(t('session_expired'));
      navigate('/', { replace: true });
    };

    if (!nonce) {
      bounceToLogin('no_nonce');
      return;
    }

    // The JWT arrives in the consume response, not the URL fragment — it grows
    // with the user's scenario count and oversized redirect Location headers
    // get rejected by the serving stack. The session-bound single-use nonce
    // still blocks login-CSRF: it only consumes against the session that
    // minted it, so the returned token always matches the browser's session.
    void (async () => {
      let token: string;
      try {
        const response = await axios.post<{ ok: boolean; token: string | null }>(
          CONSUME_NONCE_ENDPOINT,
          { nonce },
          { withCredentials: true, timeout: CONSUME_NONCE_TIMEOUT_MS },
        );
        if (!response.data.token) {
          bounceToLogin('no_token_returned');
          return;
        }
        token = response.data.token;
      } catch (error) {
        const code = (error as { code?: string } | null)?.code;
        bounceToLogin(
          code && AXIOS_TIMEOUT_CODES.includes(code)
            ? 'consume_nonce_timed_out'
            : 'consume_nonce_failed',
        );
        return;
      }

      const stored = setToken(token);
      if (!stored) {
        bounceToLogin('token_store_failed');
        return;
      }

      const storedNext = sessionStorage.getItem(OAUTH_NEXT_STORAGE_KEY);
      sessionStorage.removeItem(OAUTH_NEXT_STORAGE_KEY);
      const nextPath = getSafeNextPath(storedNext);
      if (storedNext) {
        // Visibility into the MCP logged-out resume via social login/signup. posthog-js
        // flushes queued events via sendBeacon on the unload window.location.assign triggers.
        track(PostHogEventEnum.OauthNextReplayed, {
          next_present: true,
          next_replayed: nextPath !== null,
        });
      }
      if (nextPath) {
        reportOutcome('redirected_to_next');
        window.location.assign(nextPath);
        return;
      }

      if (scenarioId) {
        reportOutcome('navigated_to_scenario');
        navigate(`/i/${scenarioId}/`, { replace: true });
      } else {
        reportOutcome('handoff_to_index');
        // Brand-new social-auth signup with no ChatImplementation yet.
        // SPA-navigating to `/` would mount BootstrapProvider with no
        // scenario_id and (depending on guards) flash a blank screen.
        // Defer to Django's `index()` view: it knows to redirect a
        // logged-in user with no impl to `/add_new_ai/` and create one.
        window.location.assign('/');
      }
    })();

    // An SPA unmount with the request still pending would otherwise leave this closure
    // alive to report a false abandonment for a screen the user already left.
    return () => window.removeEventListener('pagehide', reportAbandonment);
  }, [navigate, t]);

  return (
    <div
      data-testid="auth-callback-loading"
      className="flex items-center justify-center h-screen w-screen"
    >
      <Spinner className="h-8 w-8 text-muted-foreground" />
    </div>
  );
}
